import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { Alert, Button, Empty, Input, Skeleton } from "antd";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useAllRoles } from "../hooks/useRoles";
import { hasWildcardPermission, isRoleScope, type ManagedRole } from "../types";
import { ChangeDot, ScopeIcon } from "./roleVisuals";

interface RolesListPaneProps {
	selectedId?: string;
	isNew: boolean;
	/** Saqlanmagan o'zgarishi bor rol ID si yoki `"new"` — sariq nuqta uchun. */
	dirtyKey: string | null;
	draftName: string;
}

function SectionCaption({ children }: { children: ReactNode }) {
	return (
		<div className="px-2.5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[#8a929e] dark:text-[#6b7280]">
			{children}
		</div>
	);
}

const ITEM_BASE = "flex flex-col gap-0.5 rounded-lg px-2.5 py-2 no-underline transition-colors";
const ITEM_ON = "bg-[#eaf1fb] dark:bg-[#1c2b40]";
const ITEM_OFF = "hover:bg-[#f2f2ef] dark:hover:bg-[#22262b]";
const NAME_ON = "text-[#1c5cab] dark:text-[#8fb6ea]";
const NAME_OFF = "text-[#14171c] dark:text-[#e6e8eb]";

function RoleListItem({
	role,
	selected,
	dirty,
}: {
	role: ManagedRole;
	selected: boolean;
	dirty: boolean;
}) {
	const { t } = useTranslation("roles");
	const permissions = role.permissions ?? [];

	return (
		<Link
			to="/roles/$roleId"
			params={{ roleId: role.id }}
			aria-current={selected ? "page" : undefined}
			className={`${ITEM_BASE} ${selected ? ITEM_ON : ITEM_OFF}`}
		>
			<span className="flex items-center gap-1.5">
				<span
					className={`min-w-0 flex-1 truncate font-medium ${selected ? NAME_ON : NAME_OFF}`}
					title={role.id}
				>
					{role.name}
				</span>
				{dirty ? <ChangeDot /> : null}
			</span>
			<span className="flex items-center gap-1.5 text-xs leading-[18px] text-[#565e6b] dark:text-[#a3aab5]">
				{/* Doirasi NULL/noma'lum eski rol backend'da `self` deb talqin
				    qilinadi — «tanlanmagan» uni tahrirlab tanlash kerakligini bildiradi. */}
				{isRoleScope(role.scope) ? (
					<>
						<ScopeIcon scope={role.scope} className="text-[#8a929e]" />
						<span>{t(`scope.${role.scope}`)}</span>
					</>
				) : (
					<span className="text-[#8a929e]">{t("list.noScope")}</span>
				)}
				<span className="text-[#c4c8cd] dark:text-[#4b5058]">·</span>
				<span className="truncate text-[#8a929e] dark:text-[#6b7280]">
					{hasWildcardPermission(permissions)
						? t("list.allPermissions")
						: t("list.permissionCount", { count: permissions.length })}
				</span>
			</span>
		</Link>
	);
}

/**
 * Chap panel — barcha rollar (tizim / qo'shimcha), qidiruv va «Yangi rol».
 *
 * ⚠️ Qidiruv MIJOZ tomonida: rollar bitta sahifada keladi (`useAllRoles`).
 * Rol bosilishi oddiy `Link` — saqlanmagan o'zgarish bo'lsa tahrirlagichning
 * `useBlocker` i navigatsiyani to'xtatib tasdiq so'raydi.
 */
export function RolesListPane({ selectedId, isNew, dirtyKey, draftName }: RolesListPaneProps) {
	const { t } = useTranslation("roles");
	const navigate = useNavigate();
	const { data, isLoading, isError } = useAllRoles();
	const [search, setSearch] = useState("");
	const needle = useDebouncedValue(search.trim().toLocaleLowerCase(), 200);

	const roles = data?.items ?? [];
	const visible = needle
		? roles.filter(
				(role) => role.name.toLocaleLowerCase().includes(needle) || role.id.includes(needle)
			)
		: roles;
	const systemRoles = visible.filter((role) => role.isSystem);
	const customRoles = visible.filter((role) => !role.isSystem);
	const truncated = (data?.meta.total ?? 0) > roles.length;

	const renderItems = (list: ManagedRole[]) =>
		list.map((role) => (
			<RoleListItem
				key={role.id}
				role={role}
				selected={role.id === selectedId}
				dirty={dirtyKey === role.id}
			/>
		));

	return (
		<aside className="flex min-w-0 flex-col gap-0.5 border-b border-[#eeefec] bg-[#fafaf9] p-3 lg:border-r lg:border-b-0 dark:border-[#22262b] dark:bg-[#1d2126]">
			<div className="mb-1.5 flex flex-col gap-2">
				<Input
					allowClear
					prefix={<SearchOutlined className="text-[#8a929e]" />}
					placeholder={t("list.searchPlaceholder")}
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>
				<PermissionGate resource="roles" action="create">
					<Button
						type="primary"
						block
						icon={<PlusOutlined />}
						disabled={isNew}
						onClick={() => navigate({ to: "/roles/new" })}
					>
						{t("list.create")}
					</Button>
				</PermissionGate>
			</div>

			{isNew ? (
				<>
					<SectionCaption>{t("list.newSection")}</SectionCaption>
					<div className={`${ITEM_BASE} ${ITEM_ON}`}>
						<span className="flex items-center gap-1.5">
							<span className={`min-w-0 flex-1 truncate font-medium ${NAME_ON}`}>
								{draftName.trim() || t("list.draftName")}
							</span>
							{dirtyKey === "new" ? <ChangeDot /> : null}
						</span>
						<span className="flex items-center gap-1.5 text-xs leading-[18px] text-[#8a929e] dark:text-[#6b7280]">
							<PlusOutlined />
							<span>{t("list.draftHint")}</span>
						</span>
					</div>
				</>
			) : null}

			{isLoading ? (
				<Skeleton active paragraph={{ rows: 4 }} className="px-2.5 pt-2" />
			) : isError ? (
				<Alert type="error" showIcon title={t("list.loadFailed")} />
			) : visible.length === 0 ? (
				<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("list.empty")} />
			) : (
				<>
					{systemRoles.length > 0 ? (
						<>
							<SectionCaption>{t("list.systemSection")}</SectionCaption>
							{renderItems(systemRoles)}
						</>
					) : null}
					{customRoles.length > 0 ? (
						<>
							<SectionCaption>{t("list.customSection")}</SectionCaption>
							{renderItems(customRoles)}
						</>
					) : null}
				</>
			)}

			{truncated ? (
				<Alert
					type="info"
					showIcon
					className="!mt-2"
					title={t("list.truncated", { count: roles.length })}
				/>
			) : null}
		</aside>
	);
}
